import { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowDown,
  Phone,
  MessageCircle,
  Calculator,
  ShieldCheck,
  Wallet,
  Clock,
  Users,
  BadgeCheck,
  Sparkles,
  Zap,
  ChevronDown,
  ClipboardList,
  Search,
  Handshake,
} from "lucide-react";
import Seo from "@/components/site/Seo";
import { useSettings } from "@/lib/settings";
import { formatPrice } from "@/lib/format";
import { toast } from "sonner";

const PARTNERS = [
  { name: "TBI Bank", logo: "/partners/tbi.webp" },
  { name: "BT Direct", logo: "/partners/bt-direct.png" },
  { name: "UniCredit", logo: "/partners/unicredit.webp" },
  { name: "Mogo", logo: "/partners/mogo.webp" },
];

const STEPS = [
  { n: "01", icon: ClipboardList, title: "Completezi cererea", desc: "Câteva date de contact și preferințele tale de finanțare." },
  { n: "02", icon: Search, title: "Analizăm solicitarea", desc: "Comparăm ofertele partenerilor pentru cea mai bună variantă." },
  { n: "03", icon: Sparkles, title: "Primești oferta", desc: "Îți trimitem propunerea personalizată cu rate și condiții." },
  { n: "04", icon: Handshake, title: "Alegi varianta potrivită", desc: "Ne ocupăm de acte și mașina e pregătită pentru livrare." },
];

const ADVANTAGES = [
  { icon: Zap, text: "Aprobare rapidă", sub: "răspuns în 24 de ore" },
  { icon: Wallet, text: "Rate flexibile", sub: "adaptate bugetului tău" },
  { icon: MessageCircle, text: "Consultanță gratuită", sub: "explicăm fiecare pas" },
  { icon: BadgeCheck, text: "Parteneri autorizați", sub: "instituții financiare verificate" },
  { icon: ShieldCheck, text: "Fără drumuri inutile", sub: "totul online și prin telefon" },
  { icon: Clock, text: "Răspuns rapid", sub: "operatori dedicați" },
];

const FAQS = [
  { q: "Ce acte sunt necesare?", a: "În general este suficient buletinul. În funcție de partenerul financiar și de suma solicitată se poate cere și un document de venit sau extras de cont recent. Te ghidăm noi pas cu pas." },
  { q: "Pot obține finanțare fără avans?", a: "Da, în multe cazuri finanțarea este posibilă fără avans, cu documentele complete și un scoring bun. Trimite-ne cererea și îți spunem exact ce se poate în situația ta." },
  { q: "În cât timp primesc răspuns?", a: "Cel mai adesea primești un răspuns preliminar în aceeași zi. Aprobarea finală vine, de obicei, în 24-48 de ore lucrătoare, în funcție de partener." },
  { q: "Pot refinanța un credit auto existent?", a: "Da, colaborăm cu parteneri care oferă și refinanțare. Contactează-ne cu detaliile creditului actual și îți spunem dacă e avantajoasă mutarea." },
];

function computeMonthlyRate(price, advance, months, annualRate = 8.5) {
  const P = Math.max(0, price - (advance || 0));
  if (P <= 0 || !months) return 0;
  const r = annualRate / 100 / 12;
  if (r === 0) return P / months;
  return (P * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
}

const REQUIRED = ["name", "phone", "car_interest", "advance"];
const LABELS = {
  name: "Nume",
  phone: "Telefon",
  email: "Email",
  car_interest: "Mașina de interes",
  price: "Preț mașină (€)",
  advance: "Avans dorit (€)",
  period: "Perioadă (luni)",
  notes: "Alte detalii",
};

export default function Financing() {
  const { settings } = useSettings();
  const formRef = useRef(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", car_interest: "",
    price: "", advance: "", period: "60", notes: "",
  });
  const [errors, setErrors] = useState({});
  const [openFaq, setOpenFaq] = useState(0);

  // calculator state
  const [calcPrice, setCalcPrice] = useState(20000);
  const [calcAdvance, setCalcAdvance] = useState(4000);
  const [calcMonths, setCalcMonths] = useState(60);
  const monthlyRate = useMemo(
    () => computeMonthlyRate(calcPrice, calcAdvance, calcMonths),
    [calcPrice, calcAdvance, calcMonths],
  );

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: null }));
  };
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const buildMessage = (f) => {
    const line = (k, v) => (v ? `${LABELS[k]}: ${v}` : null);
    return [
      "Salut!",
      "Sunt interesat de o ofertă de finanțare auto.",
      "",
      line("name", f.name), line("phone", f.phone), line("email", f.email),
      line("car_interest", f.car_interest),
      f.price ? `Preț mașină: ${f.price} €` : null,
      f.advance ? `Avans dorit: ${f.advance} €` : null,
      f.period ? `Perioadă: ${f.period} luni` : null,
      line("notes", f.notes),
      "", "Aștept oferta dumneavoastră.", "Mulțumesc!",
    ].filter(Boolean).join("\n");
  };

  const submit = (e) => {
    e.preventDefault();
    const nextErr = {};
    for (const k of REQUIRED) if (!String(form[k] || "").trim()) nextErr[k] = "Câmp obligatoriu";
    if (Object.keys(nextErr).length) {
      setErrors(nextErr);
      toast.error("Te rugăm completează câmpurile obligatorii.");
      document.querySelector(`[name="${REQUIRED.find((k) => nextErr[k])}"]`)?.focus();
      return;
    }
    const wa = (settings.whatsapp || "0753567030").replace(/\s/g, "");
    const num = wa.startsWith("40") ? wa : `40${wa.replace(/^0/, "")}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(buildMessage(form))}`, "_blank", "noopener,noreferrer");
    toast.success("Cererea ta e pregătită în WhatsApp — apasă Trimite acolo.");
  };

  const phoneClean = (settings.phone || "0753567030").replace(/\s/g, "");

  return (
    <div className="bg-[#0a0a0a] min-h-screen pb-20">
      <Seo title="Finanțare auto · AutoRIO Suceava" description="Finanțare rapidă pentru mașina dorită prin parteneri autorizați: TBI Bank, BT Direct, UniCredit, Mogo. Aprobare online, rate flexibile, consultanță gratuită." path="/finantare" />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-24">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(60% 55% at 30% 40%, rgba(212,175,55,0.16) 0%, transparent 60%)" }} />
        <div className="relative container-wide grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.05] backdrop-blur-md px-4 py-1.5 border border-white/[0.1]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/80">Aprobare rapidă</span>
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[68px] font-extrabold text-white leading-[1.02] tracking-tight">
              Finanțare{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">simplă</span>{" "}
              pentru mașina dorită.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/65 max-w-xl leading-relaxed">
              Colaborăm cu parteneri financiari autorizați pentru a găsi cea mai bună soluție pentru tine.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button onClick={scrollToForm} className="gold-shine inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.55)]">
                Solicită finanțare <ArrowDown className="h-4 w-4" />
              </button>
              <a href={`tel:${phoneClean}`} className="inline-flex items-center gap-2 bg-transparent border border-white/25 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors">
                <Phone className="h-4 w-4" /> Sună acum
              </a>
            </div>
          </motion.div>

          {/* Right — approval process cards */}
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }} className="hidden lg:block lg:col-span-5">
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl bg-[#D4AF37]/10 blur-3xl" />
              <div className="relative rounded-3xl border border-white/[0.08] bg-gradient-to-br from-[#141210] via-[#111] to-[#0a0a0a] p-6 space-y-3">
                <ApprovalCard icon={ClipboardList} title="Cerere trimisă" status="Astăzi · 10:24" done />
                <ApprovalCard icon={Search} title="Analiză în curs" status="În lucru · 10:38" pulse />
                <ApprovalCard icon={Sparkles} title="Ofertă generată" status="Estimat · 24h" />
                <ApprovalCard icon={Handshake} title="Semnare + livrare" status="La convenire" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="container-wide py-14 sm:py-16 border-t border-white/[0.06]">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <div className="eyebrow">Cu încrederea instituțiilor</div>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
            Parteneri <span className="font-serif-italic gold-gradient-text font-medium">financiari</span>
          </h2>
        </div>
        <div className="relative rounded-2xl border border-white/[0.08] bg-[#141414] overflow-hidden">
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
          <div className="relative m-3 sm:m-4 rounded-xl bg-[#f6f2e9] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-black/[0.06]">
              {PARTNERS.map((p) => (
                <div key={p.name} className="group flex items-center justify-center h-20 sm:h-24 px-6">
                  <img src={p.logo} alt={p.name} loading="lazy"
                    className="max-h-9 sm:max-h-11 max-w-[130px] sm:max-w-[160px] w-auto object-contain opacity-80 group-hover:opacity-100 transition-all duration-500 group-hover:scale-[1.05]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-wide py-16 sm:py-24 border-t border-white/[0.06]">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <div className="eyebrow">Procesul</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Cum <span className="font-serif-italic gold-gradient-text font-medium">funcționează</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-white/[0.06] bg-[#111111] p-6 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-start justify-between mb-6">
                <span className="h-12 w-12 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-[#D4AF37]" strokeWidth={2.2} />
                </span>
                <span className="font-display text-[42px] font-extrabold leading-none select-none"
                  style={{ WebkitTextStroke: "1.5px rgba(212,175,55,0.5)", color: "transparent" }}>{s.n}</span>
              </div>
              <h3 className="font-display text-lg font-extrabold text-white leading-tight">{s.title}</h3>
              <p className="mt-2 text-[14px] text-white/55 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="container-wide py-16 sm:py-20 border-t border-white/[0.06]">
        <div className="max-w-2xl mb-10">
          <div className="eyebrow">Avantaje</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            De ce finanțezi <span className="font-serif-italic gold-gradient-text font-medium">cu noi</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ADVANTAGES.map((a, i) => (
            <motion.div key={a.text} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4, delay: i * 0.06 }}
              className="rounded-2xl border border-white/[0.06] bg-[#111111] p-6 flex items-start gap-4 hover:border-[#D4AF37]/30 transition-colors">
              <span className="h-11 w-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                <a.icon className="h-5 w-5 text-[#D4AF37]" strokeWidth={2.2} />
              </span>
              <div>
                <div className="text-white font-bold text-[16px] leading-tight">{a.text}</div>
                <div className="text-white/50 text-[13px] mt-1">{a.sub}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="container-wide py-16 sm:py-20 border-t border-white/[0.06]">
        <div className="relative rounded-3xl overflow-hidden border border-white/[0.06] bg-[#111111] p-6 sm:p-10">
          <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#D4AF37]/10 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-[#D4AF37]" />
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Calculator orientativ</div>
            </div>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Estimează rata <span className="font-serif-italic gold-gradient-text font-medium">lunară</span>
            </h2>
            <p className="mt-2 text-white/50 text-sm max-w-xl">
              Doar o estimare orientativă. Oferta reală vine de la partenerii noștri, în funcție de scoringul tău.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div className="space-y-6">
                <RangeRow label="Preț mașină" value={formatPrice(calcPrice)} min={5000} max={80000} step={500} val={calcPrice} setVal={setCalcPrice} />
                <RangeRow label="Avans" value={`${formatPrice(calcAdvance)} · ${calcPrice > 0 ? Math.round((calcAdvance / calcPrice) * 100) : 0}%`}
                  min={0} max={Math.round(calcPrice * 0.9)} step={100} val={calcAdvance} setVal={setCalcAdvance} />
                <div>
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-white/50 font-bold">
                    <span>Perioadă</span><span className="text-[#D4AF37]">{calcMonths} luni</span>
                  </div>
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {[24, 36, 48, 60].map((m) => (
                      <button key={m} onClick={() => setCalcMonths(m)}
                        className={`h-10 rounded-lg text-[13px] font-bold border transition-colors ${
                          calcMonths === m ? "bg-[#D4AF37] border-[#D4AF37] text-[#0a0a0a]" : "border-white/[0.1] text-white/80 hover:border-[#D4AF37]/50"
                        }`}>{m}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="relative rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.06] p-6">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold">Rată lunară estimată</div>
                <div className="mt-2 font-display text-5xl font-extrabold gold-gradient-text leading-none">{formatPrice(monthlyRate)}</div>
                <div className="mt-2 text-[12px] text-white/50">/lună · dobândă orientativă 8,5% p.a.</div>
                <p className="mt-4 text-[11px] text-white/45 leading-relaxed">
                  Estimare informativă. Rata reală poate diferi în funcție de partener, scoring și opțiuni.
                </p>
                <button onClick={scrollToForm} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full h-11 font-bold text-[12px] uppercase tracking-[0.16em] transition-colors">
                  Solicită ofertă reală <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section ref={formRef} className="container-wide py-16 sm:py-20 border-t border-white/[0.06] scroll-mt-28">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10">
          <div>
            <div className="max-w-2xl mb-8">
              <div className="eyebrow">Cererea ta</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-[1.05]">
                Solicită o ofertă <span className="font-serif-italic gold-gradient-text font-medium">personalizată</span>
              </h2>
              <p className="mt-4 text-white/55">Trimiterea se face direct prin WhatsApp, cu mesaj pre-completat.</p>
            </div>
            <form onSubmit={submit} noValidate className="rounded-3xl border border-white/[0.06] bg-[#111111] p-6 sm:p-8 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FinField label={LABELS.name} name="name" required value={form.name} onChange={set("name")} error={errors.name} placeholder="Prenume și nume" />
                <FinField label={LABELS.phone} name="phone" required type="tel" value={form.phone} onChange={set("phone")} error={errors.phone} placeholder="07XX XXX XXX" />
              </div>
              <FinField label={LABELS.email + " (opțional)"} name="email" type="email" value={form.email} onChange={set("email")} placeholder="nume@exemplu.ro" />
              <FinField label={LABELS.car_interest} name="car_interest" required value={form.car_interest} onChange={set("car_interest")} error={errors.car_interest} placeholder="Ex: BMW Seria 3, 2020, ~20.000 €" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FinField label={LABELS.price} name="price" type="number" value={form.price} onChange={set("price")} placeholder="20000" />
                <FinField label={LABELS.advance} name="advance" type="number" required value={form.advance} onChange={set("advance")} error={errors.advance} placeholder="4000" />
                <div>
                  <FinLabel>{LABELS.period}</FinLabel>
                  <select name="period" value={form.period} onChange={set("period")} className={finInp}>
                    {[24, 36, 48, 60].map((m) => <option key={m} value={m}>{m} luni</option>)}
                  </select>
                </div>
              </div>
              <div>
                <FinLabel>{LABELS.notes + " (opțional)"}</FinLabel>
                <textarea name="notes" value={form.notes} onChange={set("notes")} rows={3}
                  className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-4 py-3 w-full text-white placeholder-white/35 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 text-[14px] outline-none transition-colors resize-none"
                  placeholder="Detalii despre mașină, situație etc." />
              </div>
              <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full h-14 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]">
                <MessageCircle className="h-4 w-4" /> Trimite prin WhatsApp
              </button>
              <p className="text-center text-[11px] text-white/45">Nu trimitem cereri pe email. Comunicăm rapid prin WhatsApp.</p>
            </form>
          </div>
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] p-6">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37] mb-4">Cum decurge</div>
              <ul className="space-y-3.5 text-[14px] text-white/80">
                {["Trimiți cererea prin WhatsApp cu detaliile relevante.", "Un consultant AutoRIO te contactează pentru completare.", "Trimitem cererea către partenerii financiari.", "Primești oferta finală și ne ocupăm de acte."].map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-0.5 h-6 min-w-6 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] font-bold text-[11px]">{i + 1}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-wide py-16 sm:py-20 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="eyebrow">Întrebări frecvente</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Bune de <span className="font-serif-italic gold-gradient-text font-medium">știut</span>
            </h2>
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-[#111111] divide-y divide-white/[0.06] overflow-hidden">
            {FAQS.map((f, i) => (
              <div key={f.q}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-white/[0.02] transition-colors">
                  <span className="text-white font-bold text-[15px]">{f.q}</span>
                  <ChevronDown className={`h-5 w-5 text-[#D4AF37] shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                      <div className="px-6 pb-6 text-white/65 text-[14px] leading-relaxed">{f.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-wide">
        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.06] bg-gradient-to-br from-[#141210] via-[#0e0d0b] to-[#0a0a0a] px-6 py-14 sm:px-12 sm:py-20 text-center">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative max-w-2xl mx-auto">
            <div className="eyebrow">Următorul pas</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
              Ai găsit mașina <span className="font-serif-italic gold-gradient-text font-medium">potrivită</span>?
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button onClick={scrollToForm} className="gold-shine inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]">
                Solicită finanțare <ArrowRight className="h-4 w-4" />
              </button>
              <a href={`tel:${phoneClean}`} className="inline-flex items-center gap-2 bg-transparent border border-white/25 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors">
                <Phone className="h-4 w-4" /> Sună acum
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ApprovalCard({ icon: Icon, title, status, done, pulse }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-white/[0.06] bg-[#0a0a0a]/60 p-4">
      <div className={`relative h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${
        done ? "bg-[#D4AF37] text-[#0a0a0a]" : "bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37]"
      }`}>
        <Icon className="h-5 w-5" strokeWidth={2.2} />
        {pulse && <span className="absolute inset-0 rounded-xl border border-[#D4AF37]/50 animate-ping" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-bold text-[14px] leading-tight">{title}</div>
        <div className="text-white/50 text-[12px] mt-0.5">{status}</div>
      </div>
      {done && <BadgeCheck className="h-5 w-5 text-[#D4AF37]" />}
    </div>
  );
}

function RangeRow({ label, value, min, max, step, val, setVal }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-white/50 font-bold">
        <span>{label}</span><span className="text-[#D4AF37]">{value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={val}
        onChange={(e) => setVal(parseInt(e.target.value))} className="mt-3 w-full accent-[#D4AF37]" />
    </div>
  );
}

const finInp = "bg-[#0a0a0a] border border-white/[0.08] rounded-lg h-12 px-4 w-full text-white placeholder-white/35 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 text-[14px] outline-none transition-colors";
function FinLabel({ children, error }) {
  return <label className={`block text-[11px] font-bold uppercase tracking-[0.16em] mb-2 ${error ? "text-red-400" : "text-white/60"}`}>{children}</label>;
}
function FinField({ label, name, required, error, ...rest }) {
  return (
    <div>
      <FinLabel error={error}>{label}{required && <span className="text-[#D4AF37] ml-0.5">*</span>}</FinLabel>
      <input name={name} className={`${finInp} ${error ? "!border-red-500/60" : ""}`} {...rest} />
      {error && <div className="mt-1.5 text-[12px] font-semibold text-red-400">{error}</div>}
    </div>
  );
}
